import { FC, KeyboardEvent, MouseEvent, useEffect, useState } from 'react'
import useTranslation from 'next-translate/useTranslation'
import { useRouter } from 'next/router'
import { Loader } from 'rsuite'
import omit from 'lodash/omit'
import useSWR from 'swr'
import Link from 'next/link'
import { Index, useHits, usePagination } from 'react-instantsearch'

import { Catalog } from '../../utils/types/Product'
import { getCatalog, removeHomeCategory } from '../../lib/category'
import useUrls from '../../services/useUrls'
import CatalogCounts from './CatalogCounts'
import { categoryOnMountLogic } from '../../lib/catalogFilter'
import useCategoryDataStructure from '../hooks/useCategoryDataStructure'
import { getFilterQueryIds } from '../Catalog/Pickers/util'
import { QueryParam } from '../../utils/constants'
import { getCurrentUrl } from '../../utils/urls'
import DISABLE_REVALIDATION_OPTIONS from '../../utils/swr'
import { CountryAndLocale } from '../../external'
import { ConditionalBold } from '../ConditionalWrapper'
import { getCountryAndLocaleStrings } from '../../utils/locales'

import styles from '../../styles/CategoryTreePicker.module.less'

interface CategoryTreeProps{
  categories: Catalog[]
  supplierCode?: string
}

const CategoryTree: FC<CategoryTreeProps> = ({ supplierCode, categories }) => {
  const { asPath, query, locale: countryAndLocale } = useRouter()
  const { locale } = getCountryAndLocaleStrings(countryAndLocale)
  const { t } = useTranslation('catalog')
  const { t: tUrl } = useTranslation('url')
  const { pushT } = useUrls()
  const [topLevelCounts, setTopLevelCounts] = useState<Record<string, number>>({})
  const { refine: setCurrentPage } = usePagination()

  const { results } = useHits()
  const counts1 = results?.disjunctiveFacets.find((facet) => facet.name === `categories.one.${locale}`)?.data || {}
  const counts2 = results?.disjunctiveFacets.find((facet) => facet.name === `categories.two.${locale}`)?.data || {}
  const counts3 = results?.disjunctiveFacets.find((facet) => facet.name === `categories.three.${locale}`)?.data || {}
  const counts: Record<string, number> = {
    ...counts1,
    ...counts2,
    ...counts3,
  }

  const {
    parentCategory,
    parentCategoryId,
    subCategory,
    selectedCategory,
    categoriesLoading,
  } = useCategoryDataStructure()

  const url = supplierCode ? `/${tUrl('supplier')}/${supplierCode}` : `/${tUrl('products')}`

  // To keep the selected category tree at top
  const sortCategories = [
    ...categories.filter((cat) => (cat.id) === parentCategoryId),
    ...categories.filter((cat) => (cat.id) !== parentCategoryId),
  ]

  const { data: categoryTreeData } = useSWR('categoryTreeData', () => [], DISABLE_REVALIDATION_OPTIONS)

  useEffect(() => {
    // to check correct category for supplier when categories load
    if (!supplierCode || categoriesLoading === false) {
      const updatedUrl = categoryOnMountLogic(
        parentCategory,
        selectedCategory,
        parentCategoryId,
        subCategory,
        url,
        t,
      )
      if (updatedUrl) {
        pushT(updatedUrl, undefined, { shallow: true })
      }
    }
  }, [asPath, categoriesLoading])

  const onCategoryClick = (
    e: MouseEvent<HTMLAnchorElement>,
    topLevelCat: boolean,
    cat: Partial<Catalog>,
  ) => {
    e.preventDefault()
    const currentQueries = omit(query, getFilterQueryIds(query))
    const urlWithCategory = topLevelCat
      ? `${url}/${tUrl('category')}/${cat['catalog.code']}`
      : `${url}/${tUrl('category')}/${parentCategory}/${cat['catalog.code']}`

    // Reset instantSearch pagination
    setCurrentPage(0)
    pushT({
      pathname: urlWithCategory,
      query: {
        ...currentQueries,
        // Navigate to the first pagination page on category change on the products catalog
        ...(!supplierCode && query[QueryParam.page] && { page: 1 }),
      },
    }, undefined, { scroll: false, shallow: true })
  }

  const renderNodeLabel = (
    cat: Partial<Catalog>,
    topLevelCat = true,
    parentCategoryCode: string = 'undefined',
  ) => {
    const isSelectedCategory = cat['catalog.code'] === selectedCategory
    const selectedCategoryData = getCatalog(selectedCategory, subCategory, t)

    // Use facet counts from main query for current category
    // Otherwise use separate unrefined query data
    const useMainQueryCounts = selectedCategoryData?.['catalog.label'] === cat['catalog.label']
      || selectedCategoryData?.catalog
        ?.flatMap((subCat) => [subCat, ...(subCat.catalog || [])])
        .some((subCat) => subCat['catalog.label'] === cat['catalog.label'])
    const productsCount = useMainQueryCounts
      ? counts[cat['catalog.label'] || '']
      : topLevelCounts[cat['catalog.label'] || '']
    const urlWithCategory = topLevelCat
      ? `${url}/${tUrl('category')}/${cat['catalog.code']}`
      : `${url}/${tUrl('category')}/${parentCategoryCode}/${cat['catalog.code']}`

    const handleCategorySelect = (e: MouseEvent<HTMLAnchorElement>) => (isSelectedCategory
      ? e.preventDefault
      : onCategoryClick(e, topLevelCat, cat))

    // Disable enter event on disabled category
    const handleKeyDown = (e: KeyboardEvent<HTMLAnchorElement>) => (!productsCount && e.key === 'Enter' ? e.preventDefault() : null)
    const href = getCurrentUrl(urlWithCategory, countryAndLocale as CountryAndLocale)

    const totalProducts = productsCount || 0

    if (supplierCode && totalProducts === 0) {
      return null
    }

    return (
      <Link
        href={href}
        key={href}
        shallow
        onKeyDown={handleKeyDown}
        onClick={handleCategorySelect}
        className={`${styles['category-link']} rs-btn ${!productsCount ? styles['disabled-category'] : ''} ${isSelectedCategory ? styles['active-category-link'] : ''}`}
      >
        <ConditionalBold
          p={isSelectedCategory}
          className="text-color-primary"
        >
          {t(cat['catalog.label'] || '')}
        </ConditionalBold>
        <span
          className={`${styles['product-count']} ${
            !productsCount ? styles['disabled-category'] : ''
          }`}
        >
          {totalProducts}
        </span>
      </Link>
    )
  }

  const shouldRenderChildren = (cat: Partial<Catalog>) => {
    const findInChildren = (children: Partial<Catalog>[]): boolean => (children.some(
      (ele) => shouldRenderChildren(ele),
    ))

    const hasChildren = cat?.catalog ? findInChildren(cat.catalog) : false
    return (
      cat['catalog.code'] === selectedCategory
      || hasChildren
    )
  }

  const renderChildNodes = (cat: Partial<Catalog>, level: number, parentCategoryCode?: string) => {
    if (level >= 4) return null // Don't render after level 4 category
    const isLevelThree = level === 3
    return (
      <ul
        className={styles.children}
        key={cat['catalog.code']}
        style={shouldRenderChildren(cat) ? {} : { display: 'none' }}
      >
        { cat?.catalog?.map((childNode) => (
          <li
            key={childNode.id}
            className={(shouldRenderChildren(childNode) && childNode.catalog && !isLevelThree)
              ? styles['opened-category']
              : undefined}
          >
            {renderNodeLabel(childNode, false, parentCategoryCode)}
            {renderChildNodes(childNode, level + 1, parentCategoryCode)}
          </li>
        ))}
      </ul>
    )
  }

  return (
    <>
      <Index
        indexName="products"
        indexId="unrefined_counts"
      >
        <CatalogCounts setCounts={setTopLevelCounts} />
      </Index>
      <ul className={`
        ${styles['category-tree-picker']}
        ${parentCategoryId ? styles.active : ''}`}
      >
        {categoriesLoading ? (
          <Loader content={t('common:Loading')} />
        ) : (
          removeHomeCategory(sortCategories).map((cat) => {
            // Use category tree data if available,
            // otherwise fallback to "fresh" data fetched on client
            const categoryObj = [
              ...(categoryTreeData || []),
              ...subCategory,
              ...categories,
            ].find((category: Catalog) => category['catalog.code'] === cat['catalog.code'])

            if (!categoryObj) {
              return null
            }

            return (
              <li
                key={cat['catalog.code']}
                className={styles['category-list']}
              >
                {renderNodeLabel(cat)}
                {renderChildNodes(categoryObj, 1, cat['catalog.code'])}
              </li>
            )
          })
        )}
      </ul>
    </>
  )
}

export default CategoryTree
